import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Plus } from "lucide-react";
import type { Product } from "../api";
import { Input } from "./ui/input";
import { fmtMoney } from "../lib/format";
import { cn } from "../lib/utils";

/**
 * Tovar qidiruv-kombobox — header zonasida ishlatiladi. Natijalar absolyut
 * (overlay) dropdown'da chiqadi, shuning uchun forma balandligini o'zgartirmaydi.
 * Qidiruv nom/artikul/barcode bo'yicha; manba — workshop-scoped products API.
 *
 * Ro'yxat fokusda DARHOL ochiladi (yozmasdan ham) — yozish esa uni filtrlaydi.
 * Klaviatura: ↑/↓ tanlovni suradi, Enter tanlaydi, Escape yopadi. Ko'rsatiladigan
 * qatorlar soni ataylab cheklangan (`MAX_ROWS`) — katalog katta bo'lishi mumkin,
 * filtrlash esa mijoz tomonda (products API paginatsiyasiz ro'yxat qaytaradi).
 */
const MAX_ROWS = 8;

export function ProductCombobox({
  products,
  onPick,
  onCreateNew,
  excludeIds,
  unavailable,
  disabled,
  placeholder,
}: {
  products: Product[];
  onPick: (p: Product) => void;
  /** Berilsa, mos tovar topilmaganda inline "yangi tovar yaratish" ko'rsatiladi. */
  onCreateNew?: (query: string) => void;
  excludeIds?: Set<number>;
  /**
   * Berilsa va `true` qaytarsa — tovar tanlab bo'lmaydi (masalan sotuvda omborda
   * yo'q tovar). Natijada qatorda "Omborda yo'q" ko'rsatiladi, bosib bo'lmaydi.
   * Xaridda berilmaydi (zaxira to'ldirish uchun har qanday tovar tanlanadi).
   */
  unavailable?: (p: Product) => boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  const { t } = useTranslation("ombor");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    const pool = products.filter((p) => !excludeIds?.has(p.id));
    // Bo'sh so'rov — butun (kesilgan) ro'yxat: fokusda darhol ko'rinadi.
    if (!s) return pool.slice(0, MAX_ROWS);
    return pool
      .filter((p) => `${p.nomi} ${p.artikul ?? ""} ${p.barcode ?? ""}`.toLowerCase().includes(s))
      .slice(0, MAX_ROWS);
  }, [q, products, excludeIds]);

  const trimmed = q.trim();
  const showNew = !!onCreateNew && trimmed.length > 0 && matches.length === 0;
  // Topilmadi — bo'sh dropdown emas, aniq holat. "Yangi yaratish" bor bo'lsa
  // o'sha qator xabarning o'rnini bosadi.
  const showEmpty = matches.length === 0 && !showNew;

  // Ro'yxat o'zgarganda tanlov birinchi qatorga qaytadi (aks holda ko'rsatkich
  // endi mavjud bo'lmagan indeksda qolib ketardi).
  useEffect(() => {
    setActive(0);
  }, [q, open]);

  function pick(p: Product) {
    onPick(p);
    setQ("");
    setOpen(false);
  }

  function createNew() {
    onCreateNew?.(trimmed);
    setQ("");
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open) {
      if (e.key === "ArrowDown") {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (matches.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      const next = (active + step + matches.length) % matches.length;
      setActive(next);
      listRef.current
        ?.querySelectorAll("[data-row]")
        [next]?.scrollIntoView({ block: "nearest" });
      return;
    }
    if (e.key === "Enter") {
      // Forma ichida — Enter submit qilib yubormasligi kerak.
      if (matches.length > 0) {
        const p = matches[active];
        if (p && !(unavailable?.(p) ?? false)) {
          e.preventDefault();
          pick(p);
        }
        return;
      }
      if (showNew) {
        e.preventDefault();
        createNew();
      }
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder ?? t("shared.combobox.placeholder")}
        className="pl-9"
        disabled={disabled}
        inputMode="search"
        role="combobox"
        aria-expanded={open}
        aria-controls="product-combobox-list"
        aria-autocomplete="list"
        aria-activedescendant={
          open && matches.length > 0 ? `product-combobox-row-${active}` : undefined
        }
      />
      {open && (
        <div
          ref={listRef}
          id="product-combobox-list"
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-64 overflow-y-auto rounded-md border bg-card shadow-lg"
        >
          {matches.map((p, i) => {
            const off = unavailable?.(p) ?? false;
            return (
              <button
                key={p.id}
                id={`product-combobox-row-${i}`}
                data-row
                role="option"
                aria-selected={i === active}
                type="button"
                disabled={off}
                className={cn(
                  "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm",
                  off ? "cursor-not-allowed opacity-60" : "hover:bg-accent",
                  i === active && !off && "bg-accent",
                )}
                onMouseEnter={() => setActive(i)}
                onClick={() => !off && pick(p)}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{p.nomi}</span>
                  {p.artikul && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {t("shared.combobox.brend")}: {p.artikul}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-right text-xs tabular-nums">
                  {off ? (
                    <span className="text-destructive">{t("shared.combobox.outOfStock")}</span>
                  ) : (
                    <span className="block text-muted-foreground">
                      {Number(p.joriy_ostatka)} {p.birlik}
                    </span>
                  )}
                  <span className="block text-muted-foreground">{fmtMoney(p.sotuv_narx)}</span>
                </span>
              </button>
            );
          })}
          {showEmpty && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t("shared.combobox.notFound")}
            </div>
          )}
          {showNew && (
            <button
              type="button"
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-primary hover:bg-accent",
                matches.length > 0 && "border-t",
              )}
              onClick={createNew}
            >
              <Plus className="size-4 shrink-0" />
              <span className="truncate">{t("shared.combobox.createNew", { query: trimmed })}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
